import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Package, AlertCircle, ShoppingCart, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import ProductImage from "./ProductImage";
import WishlistButton from "./WishlistButton";

export default function ProductCard({ product, index = 0 }) {
  const { addToCart } = useCart();
  const [addedToCart, setAddedToCart] = useState(false);
  
  const hasImage = product.images && product.images.length > 0;
  const hasSale = product.sale_price && product.sale_price < product.price;

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(price);
  };

  const getCategoryLabel = (category) => {
    const labels = {
      maxim: "Maxim",
      pkm: "PKM",
      "mp35-bergmann": "MP-35 Bergmann",
      ak: "AK",
      merch: "Merchandise",
    };
    return labels[category] || category;
  };

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (product.in_stock) {
      addToCart(product, 1);
      setAddedToCart(true);
      toast.success(`${product.name} added to cart!`);
      setTimeout(() => setAddedToCart(false), 2000);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="card-tactical group"
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/shop/${product.id}`}>
        {/* Image Container */}
        <div className="relative aspect-square overflow-hidden bg-[#1c1c1c]">
          {hasImage ? (
            <ProductImage
              src={product.images[0]}
              alt={product.name}
              className="w-full h-full object-cover product-image-hover group-hover:scale-105 transition-transform duration-500"
              retryCount={3}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-[#121212]">
              <Package className="w-16 h-16 text-[#3f3f46]" />
            </div>
          )}

          {/* Badges */}
          <div className="absolute top-3 left-3 flex flex-col gap-2">
            {hasSale && (
              <span className="px-2 py-1 bg-[#C0A062] text-black text-xs font-mono uppercase tracking-wider">
                Sale
              </span>
            )}
            {!product.in_stock && (
              <span className="px-2 py-1 bg-red-500/90 text-white text-xs font-mono uppercase tracking-wider">
                Sold Out
              </span>
            )}
          </div>

          {/* Category Badge + Wishlist */}
          <div className="absolute top-3 right-3 flex flex-col items-end gap-2">
            <span className="category-badge">{getCategoryLabel(product.category)}</span>
            <WishlistButton productId={product.id} variant="icon" />
          </div>

          {/* Hover Overlay */}
          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-3">
            <Button
              className="btn-primary transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300"
              data-testid={`view-details-${product.id}`}
            >
              View Details
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="p-5">
          <h3 className="font-heading font-bold text-white text-lg uppercase tracking-tight mb-2 line-clamp-2 group-hover:text-[#C0A062] transition-colors">
            {product.name}
          </h3>
          
          <p className="text-[#A1A1AA] text-sm line-clamp-2 mb-4">
            {product.description}
          </p>

          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              {hasSale ? (
                <>
                  <span className="price-current">{formatPrice(product.sale_price)}</span>
                  <span className="price-original">{formatPrice(product.price)}</span>
                </>
              ) : (
                <span className="price-current">{formatPrice(product.price)}</span>
              )}
            </div>

            {product.in_stock ? (
              <span className="stock-badge in-stock">
                <span className="w-1.5 h-1.5 rounded-full bg-[#8CB369] mr-1.5"></span>
                In Stock
              </span>
            ) : (
              <span className="stock-badge out-of-stock">
                <AlertCircle className="w-3 h-3 mr-1" />
                Sold Out
              </span>
            )}
          </div>

          {/* Add to Cart Button */}
          {product.in_stock && (
            <Button
              className="btn-secondary w-full"
              onClick={handleAddToCart}
              disabled={addedToCart}
              data-testid={`add-to-cart-${product.id}`}
            >
              {addedToCart ? (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Added!
                </>
              ) : (
                <>
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Add to Cart
                </>
              )}
            </Button>
          )}
        </div>
      </Link>
    </motion.div>
  );
}
